/* Texte für Export-Dateien aus einer Übersetzungsfunktion zusammenstellen.
 *
 * Die Funktion `t` ist die von vue-i18n (oder ein gleichwertiger Stellvertreter in Skripten und
 * Tests). Hier steht keine Sprache — nur welche Schlüssel gebraucht werden.
 */

import { fmtDate } from '../domain/format.js';
import { localIsoDate } from '../domain/playerListExport.js';

/**
 * @param {(key: string, params?: object) => string} t
 * @param {{ table: { scope: string, total: number }, date?: Date, title?: string }} ctx  title = freier Listentitel
 *   (schon bereinigt); leer = Standardtitel
 * @returns Texte für renderPlayerListPdf und buildCsv ({ header, csvGroup } sind die Teile für die CSV)
 */
export function exportLabels(t, { table, date = new Date(), title = '' }) {
  return {
    title: title || t('playerExport.pdf.title'),
    scope: t(`playerExport.scope.${table.scope}`),
    date: fmtDate(localIsoDate(date)),
    countLabel: t('playerExport.pdf.countLabel'),
    publisher: t('playerExport.pdf.publisher'),
    /* Listen-PDF: "For internal use only." plus derselbe Satz zu Minderjährigen wie im Profil-PDF (eine Quelle:
       PROFILE_LABELS.minorsNotice). Die CSV bleibt ohne Hinweis, damit sie sich in Excel sauber öffnen lässt. */
    footer: `${t('playerExport.pdf.footer')} ${PROFILE_LABELS.minorsNotice}`,
    transferred: t('playerExport.group.transferred'),
    header: (key) => t(`playerExport.col.${key}`),
    group: (group) => t(`positionGroup.${group}`),
    page: (page, total) => t('playerExport.pdf.page', { page, total }),
    csvGroup: (kind) => t(`playerExport.group.${kind}`),
  };
}

/* Beschriftung der Heatmap auf der Player Card.
   Die Heatmap ist eine Vorlage nach Position, keine Messdaten. Erst wenn sie aus echten X11-Daten kommt, darf hier
   'TOUCH DENSITY' stehen. Entscheidung Bastian 2026-10-14. Der Wortlaut 'Touch Density' darf bis dahin in keinem PDF stehen. */
export const HEATMAP_LABEL = 'Heat map · Position zones';

/**
 * Texte des Profil-PDFs (Share > Profile PDF). Fest Englisch, unabhängig von der Oberflächensprache: das Blatt geht
 * an internationale Empfänger. Eine Stelle zum Ändern — auch der Hinweistext in der Fußzeile.
 * Der Wortlaut des Hinweises ist von Bastian zu bestätigen.
 */
export const PROFILE_LABELS = {
  title: 'Player profile',
  /* Kleinschrift neben dem AFH-Logo im Kopf der Player Card */
  kicker: 'Player profile',
  brand: 'AFH Hub',
  /* Adresse der App in der Fußzeile der Player Card: Text und Link-Ziel */
  hubHost: 'hub.africanfootballholding.com',
  hubUrl: 'https://hub.africanfootballholding.com',
  born: 'Born',
  position: 'Position',
  height: 'Height',
  foot: 'Preferred foot',
  nationality: 'Nationality',
  nationalTeam: 'National team',
  club: 'Club',
  since: 'since', // Unterzeile der Club-Kachel: "since 09.2021"
  shirt: 'Shirt number · Season',
  highlight: 'Highlight video',
  material: 'Video material',
  highlightLabel: 'Highlight',
  matches: 'Best matches',
  playVideo: 'Play video',
  emptyNote: 'More details appear here as soon as position, material and keywords are added.',
  moreMatches: (n) => `+ ${n} more`,
  /* Transferhistorie (F13): Art wie in der App (transferHistory.kind.* in en.json), fest Englisch */
  transfers: 'Transfer history',
  transferKind: { permanent: 'Permanent', loan: 'Loan', loan_end: 'End of loan' },
  moreTransfers: (n) => `+ ${n} earlier transfer${n === 1 ? '' : 's'}`,
  /* Beschriftung unter der Heatmap: eine Vorlage nach Position, KEIN Messwert */
  heatCaption: HEATMAP_LABEL,
  transfermarkt: 'Transfermarkt',
  /* Satz zu Minderjährigen: gemeinsame Quelle für Player Card und Listen-PDF */
  minorsNotice: 'Contains personal data of minors.',
  confidential: 'Confidential. Contains personal data of minors.',
};

/** Erstellungsdatum für die Fußzeile der Karte: TT.MM.JJJJ */
export function profileDate(date = new Date()) {
  return fmtDate(localIsoDate(date));
}

/** Fußzeile links: "AFH Hub · hub.africanfootballholding.com" (die Adresse zeichnet der Renderer als Link) */
export function profileFooter() {
  return `${PROFILE_LABELS.brand} · ${PROFILE_LABELS.hubHost}`;
}
